// The pending-change model shared by the Master Data sheets (DR-147, DR-152). Memory only: nothing
// is written to browser storage, and nothing reaches PostgreSQL before review and confirmation.
// Stored records are identified only by `unitId` + `rowVersion`; the visible row decides where a
// paste lands, never which record it is. A blank pasted cell changes nothing; clearing is explicit.
import type { AllocationOperation, ApartmentRowStatus } from '@sivan/shared';

export type RowState = 'stored' | 'changed' | 'new' | 'placeholder';

export interface SheetRow<F extends string, C extends string> {
  /** Stable key: the unit id for stored records, `new-<uuid>` for pending new rows. */
  readonly key: string;
  readonly unitId: string | null;
  readonly rowVersion: number | null;
  /** Stored values as shown; null for new rows. */
  readonly base: Readonly<Record<F, string>> | null;
  /** Fields that cannot be changed on this row, with the reason (an error code). */
  readonly locked: Readonly<Partial<Record<F, string>>>;
  /** Entered (non-blank) values that differ from the stored ones. */
  readonly edits: Readonly<Partial<Record<F, string>>>;
  /** Explicit clears (Delete key, emptied editor, form). */
  readonly clears: readonly C[];
  readonly errors: Readonly<Record<string, string>>;
  readonly outcome: ApartmentRowStatus | null;
  /** Allocation operations of the last preview, for the review dialog. */
  readonly allocations: readonly AllocationOperation[];
  readonly serverVersion: number | null;
}

export interface DraftConfig<F extends string, C extends string, Context> {
  /** Every column key of the sheet, in order (restricted columns included), for paste mapping. */
  readonly columns: readonly string[];
  readonly isField: (key: string) => key is F;
  /** The explicit clear an emptied field means, or null when the field cannot be emptied. */
  readonly clearKey: (field: F) => C | null;
  /** Immediate feedback with the server's rules (the server re-validates everything). */
  readonly validate: (row: SheetRow<F, C>, context: Context) => Record<string, string>;
}

/** Why an edit was refused: 'not_clearable', or the row's lock reason (an error code). */
export type EditRefusal = string;

/** A batch row of any sheet: a new record, or the changed fields and clears of a stored one. */
export type DraftBatchRow<F extends string, C extends string> =
  | {
      readonly op: 'create';
      readonly clientRowId: string;
      readonly cells: Partial<Record<F, string>>;
    }
  | {
      readonly op: 'update';
      readonly clientRowId: string;
      readonly unitId: string;
      readonly rowVersion: number;
      readonly cells: Partial<Record<F, string>>;
      readonly clear: C[];
    };

/** The operations of one sheet, bound to its configuration. */
export function draftEngine<F extends string, C extends string, Context>(
  config: DraftConfig<F, C, Context>,
) {
  type Row = SheetRow<F, C>;
  const noEdits = {} as Readonly<Partial<Record<F, string>>>;
  const fields = config.columns.filter(config.isField);

  const newRow = (): Row => ({
    key: `new-${crypto.randomUUID()}`,
    unitId: null,
    rowVersion: null,
    base: null,
    locked: noEdits,
    edits: noEdits,
    clears: [],
    errors: {},
    outcome: null,
    allocations: [],
    serverVersion: null,
  });

  const isCleared = (row: Row, field: F): boolean => {
    const key = config.clearKey(field);
    return key !== null && row.clears.includes(key);
  };

  /** What the cell shows: '' when cleared, else the entered value, else the stored one. */
  const displayValue = (row: Row, key: string): string => {
    if (!config.isField(key) || isCleared(row, key)) return '';
    return row.edits[key] ?? row.base?.[key] ?? '';
  };

  const rowState = (row: Row): RowState => {
    const touched = Object.keys(row.edits).length > 0 || row.clears.length > 0;
    if (row.unitId !== null) return touched ? 'changed' : 'stored';
    return touched ? 'new' : 'placeholder';
  };

  /** Edited and cleared fields, from the row itself (sheets may have dynamic columns). */
  const changedFields = (row: Row): F[] => {
    const keys = new Set<string>(Object.keys(row.edits));
    for (const key of Object.keys(row.base ?? {}))
      if (config.isField(key) && isCleared(row, key)) keys.add(key);
    return [...keys].filter(config.isField);
  };

  const lockedReason = (row: Row, field: F): string | null => row.locked[field] ?? null;

  const revalidated = (row: Row, context: Context): Row => {
    const state = rowState(row);
    const errors =
      state === 'stored' || state === 'placeholder' ? {} : config.validate(row, context);
    return { ...row, errors, outcome: null, allocations: [], serverVersion: null };
  };

  /** Keeps exactly one empty placeholder row at the end, where new rows can be pasted or typed. */
  const withPlaceholder = (rows: Row[]): Row[] => {
    const kept = rows.filter(
      (row, index) => rowState(row) !== 'placeholder' || index === rows.length - 1,
    );
    const last = kept.at(-1);
    return last !== undefined && rowState(last) === 'placeholder' ? kept : [...kept, newRow()];
  };

  const without = (edits: Readonly<Partial<Record<F, string>>>, drop: (field: F) => boolean) =>
    Object.fromEntries(Object.entries(edits).filter(([field]) => !drop(field as F))) as Partial<
      Record<F, string>
    >;

  /** One entered value. Blank on a stored row is an explicit clear (the user emptied the cell). */
  const withValue = (row: Row, field: F, raw: string): Row | EditRefusal => {
    const locked = lockedReason(row, field);
    if (locked !== null) return locked;
    const value = raw.trim() === '' ? '' : raw;
    const clearKey = config.clearKey(field);
    const edits = without(row.edits, (f) => f === field);
    if (value === '') {
      // A field the row does not store (an additional slot it never used) holds nothing to clear.
      if (row.base === null || !Object.hasOwn(row.base, field) || row.base[field] === '')
        return { ...row, edits };
      if (clearKey === null) return 'not_clearable';
      return {
        ...row,
        edits: without(edits, (f) => config.clearKey(f) === clearKey),
        clears: row.clears.includes(clearKey) ? row.clears : [...row.clears, clearKey],
      };
    }
    if (row.base?.[field] !== value) edits[field] = value;
    return { ...row, edits, clears: row.clears.filter((c) => c !== clearKey) };
  };

  const setCell = (
    rows: readonly Row[],
    key: string,
    field: F,
    value: string,
    context: Context,
  ): { rows: Row[]; refused: EditRefusal | null } => {
    let refused: EditRefusal | null = null;
    const next = rows.map((row) => {
      if (row.key !== key) return row;
      const result = withValue(row, field, value);
      if (typeof result === 'string') {
        refused = result;
        return row;
      }
      return revalidated(result, context);
    });
    return { rows: withPlaceholder(next), refused };
  };

  /**
   * Places a checked table starting at a column of the visible row `anchorIndex`. Pasted row i
   * lands on the i-th displayed row below the anchor; rows beyond the display (or on the
   * placeholder) become new pending rows. Blank cells change nothing; restricted, locked and
   * unauthorized columns were rejected before this is called.
   */
  const applyPaste = (
    model: readonly Row[],
    displayed: readonly Row[],
    table: readonly (readonly string[])[],
    anchorIndex: number,
    anchorColumn: number,
    context: Context,
    columns: readonly string[] = config.columns,
  ) => {
    const updates = new Map<string, Row>();
    const created: Row[] = [];
    let updatedRows = 0;
    table.forEach((cells, i) => {
      const target = displayed[anchorIndex + i];
      const isNew = target === undefined || rowState(target) === 'placeholder';
      let row = isNew ? newRow() : target;
      cells.forEach((value, j) => {
        const key = columns[anchorColumn + j];
        if (key === undefined || !config.isField(key) || value.trim() === '') return;
        const next = withValue(row, key, value);
        if (typeof next !== 'string') row = next;
      });
      row = revalidated(row, context);
      if (isNew) {
        if (rowState(row) !== 'placeholder') created.push(row);
      } else {
        updates.set(row.key, row);
        if (rowState(row) === 'changed') updatedRows += 1;
      }
    });
    const merged = model.map((row) => updates.get(row.key) ?? row);
    const placeholderAt = merged.findIndex((row) => rowState(row) === 'placeholder');
    const rows =
      placeholderAt === -1
        ? [...merged, ...created]
        : [...merged.slice(0, placeholderAt), ...merged.slice(placeholderAt + 1), ...created];
    return { rows: withPlaceholder(rows), updatedRows, newRows: created.length };
  };

  /** Changed stored records (changed fields and explicit clears only) and new rows. */
  const toBatchRows = (rows: readonly Row[]): DraftBatchRow<F, C>[] =>
    rows.flatMap((row): DraftBatchRow<F, C>[] => {
      const state = rowState(row);
      if (state === 'stored' || state === 'placeholder') return [];
      const cells = Object.fromEntries(
        fields.flatMap((f) => (row.edits[f] === undefined ? [] : [[f, row.edits[f]]])),
      ) as Partial<Record<F, string>>;
      if (row.unitId === null) return [{ op: 'create', clientRowId: row.key, cells }];
      return [
        {
          op: 'update',
          clientRowId: row.key,
          unitId: row.unitId,
          rowVersion: row.rowVersion ?? 1,
          cells,
          clear: [...row.clears],
        },
      ];
    });

  /** Marks each row with the server's classification, field codes and planned allocations. */
  const applyResult = (
    rows: readonly Row[],
    response: {
      rows: readonly {
        clientRowId: string;
        status: ApartmentRowStatus;
        rowVersion: number | null;
        fields: Record<string, string>;
        allocations: readonly AllocationOperation[];
      }[];
    },
  ): Row[] => {
    const byKey = new Map(response.rows.map((result) => [result.clientRowId, result]));
    return rows.map((row) => {
      const result = byKey.get(row.key);
      if (result === undefined) return row;
      return {
        ...row,
        outcome: result.status,
        errors: result.fields,
        allocations: result.allocations,
        serverVersion: result.status === 'conflict' ? result.rowVersion : null,
      };
    });
  };

  /** Explicit choice after a version conflict: apply my change on top of the newer stored version. */
  const keepMyChange = (rows: readonly Row[], key: string): Row[] =>
    rows.map((row) =>
      row.key === key && row.serverVersion !== null
        ? { ...row, rowVersion: row.serverVersion, serverVersion: null, outcome: null, errors: {} }
        : row,
    );

  /** Drops the pending change of one row (a stored record returns to its stored values). */
  const discardRow = (rows: readonly Row[], key: string): Row[] =>
    withPlaceholder(
      rows.flatMap((row) => {
        if (row.key !== key) return [row];
        if (row.base === null) return [];
        return [{ ...row, edits: noEdits, clears: [], errors: {}, outcome: null, allocations: [] }];
      }),
    );

  const summarize = (rows: readonly Row[]) => {
    let changed = 0;
    let created = 0;
    let withErrors = 0;
    for (const row of rows) {
      const state = rowState(row);
      if (state === 'changed') changed += 1;
      if (state === 'new') created += 1;
      if ((state === 'changed' || state === 'new') && Object.keys(row.errors).length > 0)
        withErrors += 1;
    }
    return { changed, created, withErrors };
  };

  return {
    fields,
    newRow,
    isCleared,
    displayValue,
    rowState,
    changedFields,
    lockedReason,
    withPlaceholder,
    setCell,
    clearCell: (rows: readonly Row[], key: string, field: F, context: Context) =>
      setCell(rows, key, field, '', context),
    applyPaste,
    toBatchRows,
    applyResult,
    keepMyChange,
    discardRow,
    summarize,
  };
}
